import { useApiLazyQuery } from '@common/hooks';
import DataService from '@core/services/DataService';
import { getGenericLine, getLineCategory } from '@utils/helper-utils';

import {
  downloadAmendmentGroupMedicalPremiumDetails,
  downloadAmendmentMedicalPremiumDetails,
  downloadAmendmentTravelPremiumDetails
} from '../services';

import { GenericLines, LineCategory } from 'src/enums';

interface DownloadAmendmentPremiumDetailsVariables {
  amendmentId: string;
}

export function useDownloadAmendmentPremiumDetails(
  lineCode: string,
  amendmentSubTypeCode?: string
) {
  const lineCategory = getLineCategory(lineCode);
  const genericLine = getGenericLine(lineCode);
  const isCertificateAmendment =
    genericLine === GenericLines.MEDICAL_GROUP && amendmentSubTypeCode === 'EC11';
  const queryType = isCertificateAmendment
    ? LineCategory.Medical
    : genericLine === GenericLines.MEDICAL_GROUP
      ? 'groupMedical'
      : lineCategory;

  const selectQuery = (): any => {
    switch (queryType) {
      case 'groupMedical':
        return downloadAmendmentGroupMedicalPremiumDetails;
      case LineCategory.Medical:
        return downloadAmendmentMedicalPremiumDetails;
      case LineCategory.Travel:
        return downloadAmendmentTravelPremiumDetails;
      default:
        return downloadAmendmentMedicalPremiumDetails;
    }
  };

  const selectedQuery = selectQuery();

  const [downloadApi] = useApiLazyQuery<
    any,
    DownloadAmendmentPremiumDetailsVariables
  >(selectedQuery, 'downloadAmendmentPremiumDetails', undefined, {
    withLoader: false,
    successMsg: 'Premium details were downloaded successfully.'
  });

  const downloadDocument = async (
    variables: DownloadAmendmentPremiumDetailsVariables
  ): Promise<void | null> => {
    if (!selectedQuery) return null;

    try {
      const response = await downloadApi({ variables });
      const file =
        response?.data?.production?.[Object.keys(response.data.production)[0]];

      if (!file?.content) return null;

      DataService.downloadBase64File(
        file.content,
        file.contentType,
        file.fileName
      );
    } catch (err) {
      console.error('Error downloading amendment premium details:', err);

      return null;
    }
  };

  return downloadDocument;
}
